import { CeruleanButton, GrayDarkButton } from 'components/Button'
import I18n from 'components/I18n'
import Money from 'components/Money'
import { actions, selectors } from 'data'
import { Position } from 'data/Order/types'
import messages from 'lang'
import React, { useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { SideType } from 'scenes/MarketData/types'
import { getOppositeSide } from 'scenes/MarketData/utils'
import styled from 'styled-components'
import { getBaseCurrency } from 'utils/pairs'
import { color } from 'utils/styledHelper'

import {
  AmountInput,
  Footer,
  Header,
  LimitPriceInput,
  MarginModalSwitchLabel,
  MarginModalSwitchWrapper,
  Wrapper
} from './styles'

const Description = styled.p<{ side: SideType }>`
  > b {
    text-transform: uppercase;
    color: ${(p) => (p.side === 'buy' ? color('green600') : color('red600'))};
  }
`

const getAmountError = (amount: string, max: number, min: number, currency: string) => {
  const numAmount = Number(amount)
  if (amount === '' || Number.isNaN(numAmount)) return undefined
  if (!amount || numAmount < 0) return <I18n data={messages.validation_required} />
  if (numAmount > max) return <I18n data={messages.close_position_low_amount} />
  if (numAmount < min)
    return (
      <I18n
        data={messages.close_position_large_amount}
        min={<Money amount={min} currency={currency} />}
      />
    )
  return undefined
}

const TOLERANCE = 0.01
const getPriceError = (price: string, position: Position) => {
  const numPrice = Number(price)
  if (price === '' || Number.isNaN(numPrice)) return undefined
  if (numPrice < 0) return <I18n data={messages.validation_required} />
  if (position.side === 'buy' && numPrice < position.liquidationPrice * (1 - TOLERANCE))
    return <I18n data={messages.close_position_low_limit_price} />
  const max = position.liquidationPrice * (1 + TOLERANCE)
  if (position.side === 'sell' && position.liquidationPrice > 0 && numPrice > max)
    return (
      <I18n
        data={messages.validation_max_amount}
        field={<I18n data={messages.label_price} />}
        maxAmount={max}
      />
    )
  return undefined
}

type Props = ConnectedProps<typeof connector>

const CloseMarginPosition = ({
  amountDecimals,
  extra,
  lastTrade,
  min,
  onCancel,
  onSubmit,
  position,
  positionOrders
}: Props) => {
  const defaultType = extra?.type ?? 'limit'
  const [type, setType] = useState<'market' | 'limit'>(defaultType)
  const [amount, setAmount] = useState<string>('')
  const [price, setPrice] = useState<string>(lastTrade ? String(lastTrade) : '')

  if (!position) return null

  const max = position.qty - positionOrders.reduce((qtySum, order) => qtySum + order.orderQty, 0)
  const amountError = getAmountError(amount, max, min, getBaseCurrency(position.symbol))
  const priceError = getPriceError(price, position)

  const buttonDisabled =
    amount === '' ||
    amountError !== undefined ||
    (type === 'limit' && price === '') ||
    (type === 'limit' && priceError !== undefined)

  return (
    <Wrapper>
      <Header>
        <span>
          <I18n data={messages.margin_close_position} />
        </span>
        <MarginModalSwitchWrapper background={color('grey100')}>
          <MarginModalSwitchLabel
            data-e2e='closeCollateralMarketTypeButton'
            onClick={() => setType('market')}
            selected={type === 'market'}
          >
            <I18n data={messages.market} />
          </MarginModalSwitchLabel>
          <MarginModalSwitchLabel
            data-e2e='closeCollateralLimitTypeButton'
            onClick={() => setType('limit')}
            selected={type === 'limit'}
          >
            <I18n data={messages.order_type_limit} />
          </MarginModalSwitchLabel>
        </MarginModalSwitchWrapper>
      </Header>

      <Description side={getOppositeSide(position.side)}>
        <I18n
          data={messages.margin_close_description}
          side={<b>{getOppositeSide(position.side)}</b>}
        />
      </Description>

      {type === 'limit' ? (
        <LimitPriceInput
          currency={position.collateralCurrency}
          input={{
            onChange: setPrice,
            value: price
          }}
          meta={{
            dirty: price !== '',
            error: priceError
          }}
        />
      ) : null}

      <AmountInput
        available={max}
        currency={getBaseCurrency(position.symbol)}
        quantity={{
          input: {
            onChange: setAmount,
            value: amount
          },
          meta: {
            dirty: amount !== '',
            error: amountError,
            touched: true
          }
        }}
        min={min}
        precision={amountDecimals}
      />

      <Footer>
        <GrayDarkButton onClick={onCancel}>
          <I18n data={messages.button_cancel} />
        </GrayDarkButton>
        <CeruleanButton
          disabled={buttonDisabled}
          onClick={() => onSubmit(position, amount, type, price)}
        >
          <I18n data={messages.margin_close_position} />
        </CeruleanButton>
      </Footer>
    </Wrapper>
  )
}

const mapStateToProps = (state) => {
  const modalOptions = selectors.app.getModalOptions(state)
  const position = selectors.order
    .getOpenMarginPositions(state)
    .find((pos) => String(pos.positionId) === String(modalOptions.id))
  const pair = position?.symbol ?? 'BTC-USD'
  const lastTrade = selectors.ticker.getAll(state)[pair].last_trade_price
  const positionOrders = selectors.order
    .getOpenOrders(state)
    .filter((p) => p.positionId === position?.positionId)
  const instrument = selectors.instrument.getInstruments(state).find((i) => i.symbol === pair)
  return {
    amountDecimals: instrument?.volume_increment ?? 2,
    extra: modalOptions.extra,
    lastTrade,
    min: instrument?.min_order_size_calc ?? 0,
    position,
    positionOrders
  }
}

const mapDispatchToProps = (dispatch) => ({
  onCancel: () => dispatch(actions.app.hideModal('CLOSE_MARGIN')),
  onSubmit: (position: Position, orderQty, ordType, price) => {
    dispatch(
      actions.order.closeMarginPosition({
        ordType,
        orderQty,
        positionId: position.positionId,
        price: ordType === 'limit' ? price : undefined
      })
    )
    dispatch(actions.app.hideModal('CLOSE_MARGIN'))
  }
})

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(CloseMarginPosition)
